import { Activity, ReactNode, useCallback } from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useReactiveVar } from '@apollo/client/react';
import { reactiveVars } from '@brainpool/apollo-connect';
import CustomiseColumns from '../ModalContent/CustomiseColumns/CustomiseColumns';
import { ModalProps } from '../../types/ModalTypes';
import DeleteModal from '../ModalContent/Delete/DeleteModal';
import ShareModal from '../ModalContent/Share/ShareModal';
import { DueDiligenceUploadModalContent } from '../ModalContent/DueDilModalContent/DueDilModalContent';
import PermissionsModal from '../ModalContent/Permissions/PermissionsModal';

export interface PageContainerProps {
  children: ReactNode;
  title?: string;
  description?: string;
  testid?: string;
  boxed?: boolean;
}

export function PageContainer({ children, title, description, testid, boxed }: PageContainerProps) {
  const modalData = useReactiveVar<ModalProps>(reactiveVars.modalData);

  const handleClose = useCallback(() => {
    reactiveVars.modalData({ open: false });
  }, []);

  const isShowModal = (type: ModalProps['type']) => {
    return modalData.open && modalData.type === type;
  };

  return (
    <>
      <Grid
        display="flex"
        sx={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%', alignItems: 'center' }}
        flexDirection="column"
        className="tunedd-page-container"
        data-testid={testid}
      >
        <Container
          sx={{
            maxWidth: boxed ? '910px !important' : '100%',
            width: '100%',
            bgcolor: boxed ? 'background.paper' : 'transparent',
            my: 4,
            pb: 4,
          }}
        >
          {(title || description) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 4,
              }}
            >
              <Box className="tunedd-page-content">
                {title && (
                  <Typography variant="h1" sx={{ pb: 2 }}>
                    {title}
                  </Typography>
                )}
                {description && <Typography variant="body2">{description}</Typography>}
              </Box>
            </Box>
          )}
          <Box className="tunedd-page-content"> {children}</Box>
        </Container>
      </Grid>
      {/* Modals */}
      <Activity mode={isShowModal('customise') ? 'visible' : 'hidden'}>
        <CustomiseColumns
          handleClose={handleClose}
          open={isShowModal('customise')}
          type="customise"
        />
      </Activity>
      <Activity mode={isShowModal('delete') ? 'visible' : 'hidden'}>
        <DeleteModal
          handleClose={handleClose}
          {...modalData}
          open={isShowModal('delete')}
          type="delete"
        />
      </Activity>
      <Activity mode={isShowModal('share') ? 'visible' : 'hidden'}>
        <ShareModal
          handleClose={handleClose}
          {...modalData}
          open={isShowModal('share')}
          type="share"
        />
      </Activity>
      <Activity mode={isShowModal('dueDiligence') ? 'visible' : 'hidden'}>
        <DueDiligenceUploadModalContent
          handleClose={handleClose}
          {...modalData}
          open={isShowModal('dueDiligence')}
          type="dueDiligence"
        />
      </Activity>
      <Activity mode={isShowModal('permissions') ? 'visible' : 'hidden'}>
        <PermissionsModal
          handleClose={handleClose}
          {...modalData}
          open={isShowModal('permissions')}
          type="permissions"
        />
      </Activity>
    </>
  );
}

export default PageContainer;
